import {
  Dictionary,
  LoanDocument,
  LoggedUpload,
  PreSubmitStatus,
  ProjectType,
  RequiredDocConfig,
  FundReleaseRequest,
  Adder,
  StreetAddress,
  YesNoEmpty,
  EquipmentType,
  Incentive,
  Agents,
  FundingSchedule,
  dollars,
  UploadedDoc,
  AgreementSchema,
  EnvelopeRecord
} from './index'
import type { ProductFee } from './product'
import type { ProjectCostCaps } from './project-cost-caps'

export class StatusData {
  id: string
  lookup?: string
  tenantId?: string
  tenantName?: string
  installerID?: string
  address: string
  interest: number
  interestOnlyMonths?: number
  loanDurationMonths?: number
  installationAddress?: StreetAddress
  incentive?: Incentive
  applicationReceived?: string
  nTPGiven?: string
  prequalTimestamp?: Date
  preapprovalLetter?: {
    doc: UploadedDoc
    contentMD5: string
    agreement: AgreementSchema
  }
  propertyAppraiserReviewed?: String
  docPrepQueueDate?: String
  contractReceived?: String
  authorizedInstallers?: string[]
  dueDate?: string  // date of next deadline
  bg?: string
  customerName?: string
  businessName?: string
  description?: string
  deprecated?: boolean
  uploadType?: string
  installersStatus?: string
  owner?: string
  lastModified?: string
  lastStatusChange?: Date
  docsSubmittedText?: string
  uploads?: LoggedUpload[]
  installerDocs?: Dictionary<Dictionary<LoanDocument>>
  isClosed?: boolean
  isPrequalified?: YesNoEmpty
  hasContractBeenUploaded?: boolean
  hasUpload?: LoggedUpload
  createdDate?: Date
  progress?: number
  equipmentType?: EquipmentType
  projectType?: ProjectType
  projectUrl?: string
  notesVisibleToInstallers?: string
  referralSenderEmail?: string
  incentiveSelected?: boolean
  feeCoveredBy?: 'installer' | 'consumer'
  // consumerRequestedContractChanges?: {
  //   message: string
  //   timestamp: Date
  //   resolved: boolean
  // }[]
  requiredDocuments?: RequiredDocConfig[] // from the step configuration
  requiredDocsCount?: number              // number that's actually outstanding for the installer
  approvedDocsCount?: number
  docsNeedSupportActionCount?: number
  docsNeedInstallerActionCount?: number
  fundReleasesRequested?: FundReleaseRequest[]
  finalFundReleaseAmount?: number
  fundingSchedule?: FundingSchedule
  monitoringSiteId?: string
  fundReleaseAmounts?: Record<string, number>
  installerProbationaryStatus?: string[]
  fundingBlock?: string
  isJointApp?: boolean
  coFirstName?: string
  coLastName?: string
  coPhone?: string
  coEmail?: string

  // only available for support users
  error?: string
  isEligibleForResubmit?: boolean
  submissionResponse?: any
  envelopes?: EnvelopeRecord[]
  loanNumber?: string
  useAutoDebit?: YesNoEmpty

  email?: string
  phone?: string
  updatedDate?: Date
  presubmitStatus?: PreSubmitStatus
  loanAmount?: number
  systemCost?: number
  solarCost?: number
  systemSizeKW?: number
  storageQty?: number
  // V1 new fields
  productId?: string
  monthlyAverageEnergy: number
  paydownAmount: number
  loanModFee?: number
  fees?: ProductFee[]
  // batteryCost: number
  // treeRemovalCost: number
  adders?: Adder[]
  appId?: number
  projectCostCaps?: ProjectCostCaps

  // Loan Application Phase Airtable Fields
  contractNotes: string
  loanDocumentsSentNotes: string
  loanDocumentsSignedNotes: string
  initialBankReviewNotes: string
  homeOwnershipVerificationNotes: string
  bankFinalReviewNotes: string
  loanDocsIssuedDate: string
  loanDocsSignedDate: string
  pendingBookingDate: string

  // Loan Funding Phase Airtable Fields
  roofPermittingComments: string
  passedRoofInspectionComments: string
  roofLienWaiverFormComments: string
  roofFundReleaseRequestComments: string
  roofFundingDisbursedComments: string
  roofFundingDate: string
  constructionPermitComments: string
  engineeringPlansComments: string
  initialFundReleaseRequestComments: string
  initialFundingDisbursedComments: string
  partialFundingDate: string
  proofOfPassedInspectionComments: string
  proofOfPermissionToOperateComments: string
  contractorLienWaiverFormComments: string
  finalFundingReleaseRequestComments: string
  finalFundingDisbursedComments: string
  fullFundingDate: string
  fullFundingReleaseRequestComments: string
  lenderInspectionRequestedTimestamp?: Date
  lenderInspectionType?: string
  lenderInspectionVerifiedDate?: string

  constructor(o: Partial<StatusData>) {
    Object.keys(o).forEach(k => this[k] = o[k])

    if (this.lastStatusChange) {
      this.lastStatusChange = new Date(this.lastStatusChange)
    }

    if (this.lenderInspectionRequestedTimestamp) {
      this.lenderInspectionRequestedTimestamp = new Date(
        this.lenderInspectionRequestedTimestamp,
      )
    }

    this.createdDate = new Date(this.createdDate)
    this.updatedDate = new Date(this.updatedDate)

    this.uploads = this.uploads?.map(u => {
      u.timestamp = new Date(u.timestamp)
      return u
    }) || []

    this.uploads.forEach(u => u.documents?.forEach(doc => {
      doc.history?.forEach(h => {
        h.timestamp = new Date(h.timestamp)
      })
    }))

    this.fundReleasesRequested = this.fundReleasesRequested?.map(fr => {
      fr.requestedAt = new Date(fr.requestedAt)
      return fr
    }) || []
  }

  uiSort(): void {
    this.uploads.forEach(u => u.documents?.forEach(doc => {
      doc.history?.sort((a, b) => b.timestamp.valueOf() - a.timestamp.valueOf())
    }))
    this.uploads.sort((a, b) => b.timestamp.valueOf() - a.timestamp.valueOf())
  }

  activeAgentRuns(): Agents.IWorkflowRun[] {
    return Object.values(this.installerDocs || {})
      .flatMap(slotMap => Object.values(slotMap || {}).flatMap(doc => doc?.agentWorkflowRuns || []))
      .filter(r => Agents.WorkflowRun.isActive(r))
  }

  toFormData(): any {
    return {
      ...this,
      adders: this.adders?.map(a => ({ ...a, amount: dollars(a.amount) })),
      systemCost: dollars(this.systemCost),
      loanAmount: dollars(this.loanAmount),
      solarCost: dollars(this.solarCost),
      systemSizeKW: this.systemSizeKW?.toString(),
      storageQty: this.storageQty?.toString(),
      loanModFee: this.loanModFee ? dollars(this.loanModFee) : undefined
    }  
  }

  static docForSlot(project: StatusData, slot: string): LoanDocument | undefined {
    // this may not be reliable because it's possible the same slot exists for 
    // multiple upload types, but unclear if that will only happen in testing...
    for (const slotMap of Object.values(project.installerDocs || {})) {
      for (const key of Object.keys(slotMap)) {
        if (key === slot) {
          return slotMap[key]
        }
      }
    }
  }
}

